import React, { useState } from 'react';
import 'react-phone-input-2/lib/style.css';
import countryList from 'react-select-country-list';
import Footer from '../components/accueil/Footer';
import ChatWidget from '../components/accueil/ChatWidget';
import Navbar from '../components/accueil/Navbar';
import PageBanner from './Banner';
import api from '../components/api';
import useGlobale from '../components/global';
import { toast, Toaster } from 'react-hot-toast';
import Stepper from '../components/formIn/Stepper';
import Step1 from '../components/formIn/Step1';
import Step2 from '../components/formIn/Step2';
import Step3 from '../components/formIn/Step3';

// const referenceNumber = 'B36GI65BH';
const countryOptions = countryList().getData().map(country => ({
    value: country.value,
    label: (
        <div className="flex items-center">
            <img
                src={`https://flagcdn.com/24x18/${country.value.toLowerCase()}.png`}
                className="mr-2 h-4"
                alt={country.label}
            />
            {country.label}
        </div>
    )
}));

const generateReferenceNumber = () => {
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.random().toString(36).substring(2, 5).toUpperCase();
    return `VOY-${timestamp}-${random}`;
};

const referenceNumber = generateReferenceNumber();
// État initial du formulaire
const initialFormState = {
    // Document de voyage
    document_voyage: {
        type: '',
        numero: '',
        date_expiration: '',
        fichier: null,
       pays_emeteur: 'Niger', // Par défaut
        delivre_par: ''
    },

    // Type de transport
    type_voyage: '',

    // Informations sur le séjour
    sejour: {
        motif: '',
        autre_motif: '',
        duree: '',
        ville_destination: '',
        date_debut: '',
        // Transport aérien
        compagnie_aerienne: '',
        autre_compagnie: '',
        aeroport_depart: '',
        aeroport_arrivee: '',
        date_vol: '',
        heure_vol: '',
        numero_vol: '',
        // Transport terrestre
        compagnie_terrestre: '',
        autre_compagnie_terrestre: '',
        type_vehicule: '',
        numero_vehicule: '',
        ville_depart: '',
        ville_arrivee: '',
        date_depart: '',
        heure_depart: ''
    },

    // Contact d'urgence
    contact_urgence: {
        nom: '',
        prenom: '',
        telephone: '',
        email: '',
        lien: '',
        autre_lien: ''
    },

    // Statut du voyage
    statut: 'enregistre'
};

const FormIn = () => {
    const { authenticated, user, logout } = useGlobale();
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState(initialFormState);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [savedVoyage, setSavedVoyage] = useState(null);
    const [formErrors, setFormErrors] = useState({});

    const handleNext = () => {
        const errors = validateStep(currentStep);
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            toast.error('Veuillez remplir tous les champs obligatoires');
            return;
        }
        setCurrentStep(currentStep + 1);
    };

    // Configuration des étapes
    const steps = [
        {
            number: 1,
            title: "Informations sur le voyage",
            icon: "👤"
        },
        {
            number: 2,
            title: "Contact d'urgence",
            icon: "🏠"
        },
        {
            number: 3,
            title: "Récapitulatif",
            icon: "📄"
        }
    ];

    // Gestionnaire pour les fichiers
    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 10 * 1024 * 1024) { // 10MB max
                toast.error('Le fichier est trop volumineux. Maximum 10MB');
                return;
            }
            setFormData(prev => ({
                ...prev,
                document_voyage: {
                    ...prev.document_voyage,
                    fichier: file
                }
            }));
        }
    };

    // Gestionnaire pour les changements de champs
    const handleChange = (e) => {
        const { name, value } = e.target;

        // Gestion des champs imbriqués (avec notation point)
        if (name.includes('.')) {
            const [section, field] = name.split('.');
            setFormData(prev => ({
                ...prev,
                [section]: {
                    ...prev[section],
                    [field]: value
                }
            }));

            // Nettoyage des erreurs lors de la modification
            if (formErrors[`${section}_${field}`]) {
                setFormErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[`${section}_${field}`];
                    return newErrors;
                });
            }
        } else {
            // Pour les champs de premier niveau
            setFormData(prev => ({ ...prev, [name]: value }));
            if (formErrors[name]) {
                setFormErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[name];
                    return newErrors;
                });
            }
        }
    };

    // Gestionnaire spécifique pour le téléphone
    const handlePhoneChange = (value) => {
        setFormData(prev => ({
            ...prev,
            contact_urgence: {
                ...prev.contact_urgence,
                telephone: value
            }
        }));
    };

    // Améliorez la fonction validateStep
    const validateStep = (step) => {
        const errors = {};

        if (step === 1) {
            // Validation du type de voyage
            if (!formData.type_voyage) {
                errors.type_voyage = "Type de transport requis";
            }

            // Validation du document de voyage
            if (!formData.document_voyage.type) errors.document_type = "Type de document requis";
            if (!formData.document_voyage.numero) errors.document_numero = "Numéro de document requis";
            if (!formData.document_voyage.date_expiration) errors.document_date = "Date d'expiration requise";
            if (!formData.document_voyage.pays_emeteur) errors.pays_emeteur = "Pays émetteur requis";
            if (!formData.document_voyage.delivre_par) errors.delivre_par = "Délivré par requis";

            // Validation du séjour
            if (!formData.sejour.motif) errors.motif = "Motif du voyage requis";
            if (formData.sejour.motif === 'Autre' && !formData.sejour.autre_motif) {
                errors.autre_motif = "Précision du motif requise";
            }
            if (!formData.sejour.duree) errors.duree = "Durée du séjour requise";
            if (!formData.sejour.ville_depart) errors.ville_depart = "Ville de départ requise";
            if (!formData.sejour.ville_arrivee) errors.ville_arrivee = "Ville d'arrivée requise";

            // Validation spécifique au type de transport
            if (formData.type_voyage === 'aerien') {
                if (!formData.sejour.compagnie_aerienne) errors.compagnie_aerienne = "Compagnie aérienne requise";
                if (!formData.sejour.aeroport_depart) errors.aeroport_depart = "Aéroport de départ requis";
                if (!formData.sejour.aeroport_arrivee) errors.aeroport_arrivee = "Aéroport d'arrivée requis";
                if (!formData.sejour.numero_vol) errors.numero_vol = "Numéro de vol requis";
                if (!formData.sejour.date_vol) errors.date_vol = "Date du vol requise";
                if (!formData.sejour.heure_vol) errors.heure_vol = "Heure du vol requise";
            } else if (formData.type_voyage === 'terrestre') {
                if (!formData.sejour.compagnie_terrestre) errors.compagnie_terrestre = "Compagnie terrestre requise";
                if (!formData.sejour.type_vehicule) errors.type_vehicule = "Type de véhicule requis";
                if (!formData.sejour.numero_vehicule) errors.numero_vehicule = "Numéro du véhicule requis";
                if (!formData.sejour.date_depart) errors.date_depart = "Date de départ requise";
            }
        } 
        else if (step === 2) {
            if (!formData.contact_urgence.nom?.trim()) errors.contact_nom = "Nom requis";
            if (!formData.contact_urgence.prenom?.trim()) errors.contact_prenom = "Prénom requis";
            if (!formData.contact_urgence.telephone?.trim()) errors.contact_telephone = "Téléphone requis";
            if (!formData.contact_urgence.lien) errors.contact_lien = "Lien requis";
            if (formData.contact_urgence.lien === 'Autre' && !formData.contact_urgence.autre_lien?.trim()) {
                errors.autre_lien = "Précision du lien requise";
            }
        }

        setFormErrors(errors);
        return errors;
    };

    // Sauvegarde des données
    const saveVoyageData = async () => {
        try {
            setIsSubmitting(true);
            const tokensString = localStorage.getItem('auth_tokens');
            if (!tokensString) {
                throw new Error('Non authentifié');
            }

            const tokens = JSON.parse(tokensString);
            const formDataToSend = new FormData();


            // Ajout du fichier du document
            if (formData.document_voyage.fichier) {
                formDataToSend.append('document_voyage.fichier', formData.document_voyage.fichier);
            }

            // Préparation des données JSON
            const jsonData = {
                type_voyage: formData.type_voyage,
                document_voyage: {
                    type: formData.document_voyage.type,
                    numero: formData.document_voyage.numero,
                    date_expiration: formData.document_voyage.date_expiration,
                   pays_emeteur: formData.document_voyage.pays_emeteur,
                    delivre_par: formData.document_voyage.delivre_par
                },
                contact_urgence: formData.contact_urgence,
                sejour: {
                    ...formData.sejour,
                    date_debut: formData.type_voyage === 'aerien' ?
                        formData.sejour.date_vol : formData.sejour.date_depart
                },
                statut: 'enregistre',
                reference: referenceNumber
            };

            // Ajout des données JSON au FormData
            formDataToSend.append('data', JSON.stringify(jsonData));

            const response = await api.post('/voyage/create_voyage/', formDataToSend, {
                headers: {
                    'Authorization': `Bearer ${tokens.access}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data) {
                setSavedVoyage(response.data);
                toast.success('Voyage enregistré avec succès !');
                return response.data;
            }
        } catch (error) {
            console.error('Erreur:', error);
            if (error.response?.status === 401) {
                toast.error('Session expirée, veuillez vous reconnecter');
            } else {
                toast.error(error.response?.data?.message || 'Erreur lors de la sauvegarde');
            }
            throw error;
        } finally {
            setIsSubmitting(false);
        }
    };

    // Soumission du formulaire
    // Modifiez la fonction handleSubmit
    const handleSubmit = async (e) => {
        if (e) e.preventDefault();

        if (currentStep < 3) {
            const errors = validateStep(currentStep);
            if (Object.keys(errors).length > 0) {
                setFormErrors(errors);
                toast.error('Veuillez remplir tous les champs obligatoires');
                return;
            }
            setCurrentStep(currentStep + 1);
            return;
        }

        // Validation finale
        try {
            setIsSubmitting(true);
            await saveVoyageData();
            toast.success('Formulaire soumis avec succès!');
            // Redirection ou autre action après succès
        } catch (error) {
            console.error('Erreur:', error);
            toast.error('Erreur lors de la soumission');
        } finally {
            setIsSubmitting(false);
        }
    };


    return (
        <>
            <Toaster position="top-right" />
            <Navbar authenticated={authenticated} user={user} logout={logout} />
            <PageBanner currentPage="Procédures" />
            <div className="bg-gray-50 py-8">
                <div className="container mx-auto px-4 max-w-4xl">
                    <Stepper steps={steps} currentStep={currentStep} />
                    <div className="bg-white rounded-lg shadow-lg p-8">
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {currentStep === 1 && (
                                <Step1
                                    formData={formData}
                                    setFormData={setFormData}
                                    formErrors={formErrors}
                                    handleChange={handleChange}
                                    handleFileUpload={handleFileUpload}
                                />
                            )}
                            {currentStep === 2 && (
                                <Step2
                                    formData={formData}
                                    setFormData={setFormData}
                                    formErrors={formErrors}
                                    handleChange={handleChange}
                                    handlePhoneChange={handlePhoneChange}
                                />
                            )}
                            {currentStep === 3 && (
                                <Step3
                                    formData={formData}
                                    referenceNumber={referenceNumber}
                                />
                            )}
                            <div className="flex justify-between mt-8 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
                                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                >
                                    {currentStep === 1 ? 'ANNULER' : 'RETOUR'}
                                </button>
                                <button
                                    type={currentStep === 3 ? 'submit' : 'button'}
                                    onClick={() => currentStep < 3 ? handleNext() : handleSubmit()}
                                    disabled={isSubmitting}
                                    className="px-6 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50"
                                >
                                    {isSubmitting ? (
                                        <div className="flex items-center">
                                            <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2"></div>
                                            Traitement...
                                        </div>
                                    ) : (
                                        currentStep === 3 ? 'TERMINER' : `${currentStep}/3 - ENREGISTRER ET CONTINUER`
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            <Footer />
            <ChatWidget />
        </>
    );
};

export default FormIn;